/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useBottomRightStack } from "@/components/BottomRightStack";
import useAnyDialogOpen from "@/hooks/useAnyDialogOpen";
import {
  HelpCircle, ChevronRight, ChevronLeft, Sparkles, ClipboardList, Pill, Receipt,
  Truck, Settings, MessageSquare, CreditCard, FileText, Heart, Search, Activity, Gift,
  Eye, Download, PauseCircle, LineChart, Share2, ShieldCheck, Building2,
  Zap, Youtube, Users, Camera, MessageCircleHeart, Trophy,
} from "lucide-react";

/**
 * In-app patient how-to guide. Renders as a floating purple help button
 * (bottom-right) on every authenticated patient page. Click → opens a
 * categorized guide modal with searchable sections + step-by-step walkthroughs.
 *
 * Design goals:
 *  - Discoverable but unobtrusive (small bubble, doesn't cover content)
 *  - Hidden on /onboarding and /questionnaire (those are guided flows already)
 *  - Search across all guide content
 *  - Each section has clear "What → How → Pro tip" structure
 */

const GUIDES = [
  {
    id: "switch-save",
    icon: Zap,
    title: "Switch & Save — $20 off if you're already on a GLP-1",
    keywords: ["switch", "save", "transfer", "continue", "competitor", "discount", "$20"],
    body: [
      { kind: "p", text: "Already taking Semaglutide or Tirzepatide and want to bring your prescription over to GLPFlo? We'll pick you up at the **same dose tier** with a **$20 off** Switch & Save coupon on your first order." },
      { kind: "ol", items: [
        "From the homepage or **/pricing** page, click the purple **Switch & Save** card.",
        "Or, if you have a referral link with `?promo=switch-save`, register through that — the coupon auto-applies to your account on signup.",
        "Eligibility: you were on a GLP-1 within the last 2 weeks. The coupon is good for your **first paid order only**.",
      ] },
    ],
  },
  {
    id: "new-patient-deal",
    icon: Sparkles,
    title: "New Patient Deal — first month of the Royal 3-mo tier at $79.99 / $99.99",
    keywords: ["new", "patient", "deal", "79.99", "99.99", "royal", "3-month", "first", "month", "discount"],
    body: [
      { kind: "p", text: "Brand new to GLPFlo? You can start on our **Royal 3-month tier** with a discounted first month — **$79.99 for Semaglutide** or **$99.99 for Tirzepatide**. Months 2 and 3 bill at the regular Royal-tier price. Cancel any time before the next ship for a refund minus the $25 consult fee." },
      { kind: "ol", items: [
        "On **/pricing**, scroll to the **New Patient Deal** promo card at the top of the page.",
        "Click **Start — first month of Royal 3-mo tier**.",
        "Pick your medication: **Semaglutide** or **Tirzepatide**.",
        "Confirm the attestation (first-time GLPFlo purchase, Royal 3-mo tier) and pay the discounted first month.",
        "Telehealth consult → prescription compounded by our partner pharmacy → shipped to you.",
      ] },
      { kind: "tip", text: "Eligibility: first-time GLPFlo customers only. If you've ever paid for an order with us, the deal is automatically hidden — the system enforces this at checkout." },
      { kind: "p", text: "**Non-stackable** with the Switch & Save $20-off promo or the refer-a-friend coupon." },
    ],
  },
  {
    id: "pharmacy-partners",
    icon: Building2,
    title: "Who compounds your medication — pharmacy partners",
    keywords: ["pharmacy", "polomar", "compound", "503a", "503b", "partner", "coa"],
    body: [
      { kind: "p", text: "GLPFlo prescribes — but we never touch, store, or ship your medication. Each order is fulfilled by a state-licensed, 503A/503B-compliant compounding pharmacy partner. You can meet every pharmacy we partner with on the **Our Pharmacy Partners** page." },
      { kind: "ol", items: [
        "From the footer of any page, click **Pharmacy Info** (or visit **/pharmacy-info**).",
        "Each partner has a full profile — capabilities, license number, state, and any supporting documents (Certificates of Analysis, quality reports, etc.).",
        "Every compounded batch undergoes potency verification, sterility testing, and endotoxin screening before shipping.",
      ] },
      { kind: "tip", text: "Your receipt, tracking email, and dashboard all say which pharmacy compounded your order — so you always know exactly who prepared your medication." },
    ],
  },
  {
    id: "verify-vial",
    icon: ShieldCheck,
    title: "Verify your vial — Certificate of Analysis (COA)",
    keywords: ["coa", "certificate", "analysis", "lot", "verify", "vial", "potency", "sterility", "lab", "report"],
    body: [
      { kind: "p", text: "Every compounded batch that ships to you has a **Certificate of Analysis** — an independent lab report confirming potency, sterility, endotoxin levels, and identity of the active ingredient. You can pull it up four ways:" },
      { kind: "ul", items: [
        "**Your shipping email** has a green **\"Verify your vial\"** button that opens the COA PDF for the exact lot you received.",
        "**Your shipping SMS** has a short link (`glpflo.org/c/...`) that goes to the same COA.",
        "**Your dashboard** shows a green **Verify your vial** tile under each shipped order — click to open the PDF.",
        "**The public lookup at /coa** lets anyone search by lot number. Type the digits printed on the side of your vial.",
      ] },
      { kind: "ol", items: [
        "Find the lot number on the side of your vial (looks like **LOT-99887** or just digits like `99887`).",
        "Open the COA via any link above — dashes, dots, and spaces are ignored by search so case doesn't matter.",
        "Review the report: potency, sterility, endotoxin, identity, expiration date.",
      ] },
      { kind: "tip", text: "If a COA isn't showing yet, it's usually because the pharmacy hasn't uploaded the lab report yet — typically within 1 business day of shipment. Your dashboard tile will switch from gray (\"pending\") to green automatically once it's available. You'll never get a broken link from us — we only send a COA link when one's actually on file." },
    ],
  },
  {
    id: "coa-missing",
    icon: Search,
    title: "I can't find my Certificate of Analysis",
    keywords: ["coa missing", "no certificate", "lot not found", "can't find coa", "verify vial broken"],
    body: [
      { kind: "p", text: "A few reasons your COA might not be showing yet — here's how to tell which applies:" },
      { kind: "ul", items: [
        "**Just shipped (most common).** COAs publish within ~1 business day of the shipping label. Tracking number fresh? Wait a day and refresh your dashboard.",
        "**Lot number typo.** Search by just the digits on the vial (e.g. `99887`). Our search ignores dashes, dots, and spaces.",
        "**Lot pre-2026.** A small number of vials shipped before our COA program launched in early 2026 don't have a public certificate yet. Call **(727) 516-2142** or chat us — we'll email you the lab report directly.",
        "**Lot was re-verified.** If a lot was reissued, the old COA is replaced with the new one. The new lot number is in your shipping email.",
      ] },
      { kind: "tip", text: "Still stuck? Click the chat bubble (bottom-right) and type \"Why can't I find my COA?\" — our bot has a step-by-step walkthrough and can email you the report if it's pre-2026." },
    ],
  },
  {
    id: "identity-verification",
    icon: Camera,
    title: "Identity verification — quick and secure",
    keywords: ["id", "identity", "verification", "govid", "license", "passport", "video", "selfie", "attestation"],
    body: [
      { kind: "p", text: "Federal telehealth rules require us to confirm your identity before a prescription is sent to the pharmacy. It takes about a minute and is required only once." },
      { kind: "ol", items: [
        "Open **Profile → Identity Verification** (or you'll be prompted automatically before your first order).",
        "Choose your ID type: **driver's license**, **state ID**, or **passport**.",
        "Snap a clear photo of the front of your ID and enter the ID number.",
        "Our clinical care team reviews the upload — usually within a business day — and your dashboard flips to a green **\"Verified\"** badge automatically.",
      ] },
      { kind: "tip", text: "Your ID photo is encrypted at rest and only accessible to our verification team. If your photo can't be read, we'll message you to re-upload — no penalty, just capture it in better lighting." },
    ],
  },
  {
    id: "video-library",
    icon: Youtube,
    title: "GLPFlo Video Library — how-tos & medication guides",
    keywords: ["video", "youtube", "how to", "guide", "injection", "storage", "tutorial", "library"],
    body: [
      { kind: "p", text: "Our YouTube channel publishes short patient-education videos: injection technique, storage do's-and-don'ts, side-effect tips, and clinician Q&A. The full library is built right into your account." },
      { kind: "ol", items: [
        "Open **Profile → GLPFlo Video Library**.",
        "Search by keyword (e.g. \"injection\", \"storage\", \"side effects\").",
        "Click any thumbnail to play inline — you never have to leave the GLPFlo experience.",
        "On your phone, the same library appears in the Companion App and there's a one-tap **GLPFlo on YouTube** card if you'd rather watch in the YouTube app.",
      ] },
      { kind: "tip", text: "Featured videos (gold ⭐ badge) are hand-picked by our clinical team for new patients. Start there if you just received your first shipment." },
    ],
  },
  {
    id: "stepathon-rewards",
    icon: Trophy,
    title: "Step-a-Thon Rewards — turn your steps into perks",
    keywords: ["stepathon", "step-a-thon", "rewards", "points", "badge", "challenge", "walk", "100 million", "million mile", "coupon", "redeem", "tier"],
    body: [
      { kind: "p", text: "Every step you log on a GLPFlo Step-a-Thon — manual or via the in-browser pedometer — earns you **1 point per step** in your personal rewards locker. Points roll up across every event you participate in (lifetime, never expire) and you can spend them on real perks: refill coupons, free shipping, $25–$100 refill credits, and limited-edition GLPFlo merch." },
      { kind: "ol", items: [
        "Open **/stepathon** to see the active challenges (e.g., 100 Million Step Challenge).",
        "Tap a challenge → use the **Live Walk · in-browser pedometer** while you walk, or paste yesterday's number from Apple Health / Google Fit / Fitbit into the manual entry field.",
        "As you cross milestone thresholds (Mover → Challenger → Champion → Legend → Elite → Hall of Fame on the 7-day template, or Bronze → Platinum on the 1-day template), your tier auto-upgrades.",
        "Hit a tier and the **Shareable badge** card unlocks — tap **Download PNG** to save it, or **Share** to one-tap post it to Instagram / X / iMessage.",
        "Open **/stepathon/rewards** (linked from any challenge page or the Companion App) to see your **available points**, browse the **redeemable rewards catalogue**, and redeem with one tap. Coupon codes appear instantly in your redemption history (tap to copy).",
      ] },
      { kind: "tip", text: "You don't need an active subscription to participate — any GLPFlo account works. Earn now, spend later when you order your next refill." },
      { kind: "tip", text: "Refill credits ($25 / $100) auto-apply at checkout. Coupon codes (WALK10, STEPSHIP) paste at checkout. Merch redemptions trigger a confirmation email from our team to capture sizing." },
    ],
  },
  {
    id: "share-your-story",
    icon: MessageCircleHeart,
    title: "Share Your Story — 14 days post-shipment prompt",
    keywords: ["testimonial", "review", "story", "share", "video", "feedback", "two weeks", "14 days"],
    body: [
      { kind: "p", text: "About 14 days after your first shipment ships, you'll get a short email + text asking how the first two weeks have been treating you. This is the sweet spot — long enough to have done a couple of injections and feel results, fresh enough that the experience is still vivid." },
      { kind: "ol", items: [
        "Tap the link in the SMS (looks like `glpflo.org/s/...`) or the **Share Your Story** button in the email.",
        "Rate your experience 1–5 stars, jot a short note, or upload a quick video clip (30 seconds is plenty).",
        "Choose whether you're okay with us featuring your story publicly on the website / YouTube channel — you control what we show.",
      ] },
      { kind: "tip", text: "You only get prompted once. If you'd rather not, just close the tab — no follow-up nags. If you want to share later anyway, your account profile has a Share Your Story link that's always available." },
    ],
  },
  {
    id: "faq",
    icon: HelpCircle,
    title: "Frequently Asked Questions",
    keywords: ["faq", "questions", "help", "answers"],
    body: [
      { kind: "p", text: "Our FAQ covers the most common questions — pricing, cancellation, state availability, side effects, refills, storage, and more." },
      { kind: "ol", items: [
        "Click **Help & FAQ** from your profile, or visit **/faq** directly.",
        "Use the search box or expand any question to see the answer.",
        "Still stuck? Open the chat bubble (bottom-right) or email customer.service@glpflo.com.",
      ] },
    ],
  },
  {
    id: "first-time",
    icon: Sparkles,
    title: "First time here? Start here",
    keywords: ["start", "first", "new", "welcome"],
    body: [
      { kind: "p", text: "Welcome to GLPFlo. Here's what your first visit looks like:" },
      { kind: "ol", items: [
        "Complete the medical questionnaire (about 5 minutes — answer honestly, your provider relies on it).",
        "Pick your medication and tier (Single vial · Monthly · Royal 56-day) on the Medications page.",
        "Enter your shipping address, then pay securely on **Stripe Checkout** — you're taken to Stripe's own page to enter your card, so GLPFlo never sees or stores your card number.",
        "A licensed provider reviews your request, typically within 24 hours.",
        "Once approved, your prescription is sent to Polomar Specialty Pharmacy who ships your medication directly.",
      ] },
      { kind: "tip", text: "Provider review and follow-up consultations are always included — there's never an extra charge for talking to your clinician." },
    ],
  },
  {
    id: "companion-app",
    icon: Activity,
    title: "Companion App — free rewards + tracking on the go 📱",
    keywords: ["companion", "app", "mobile", "calories", "exercise", "food", "workout", "log", "steps", "rewards", "reminders", "free"],
    body: [
      { kind: "p", text: "The **Companion App** is a free mobile-first PWA — anyone can install it, no subscription required. It's split into a **free tier** (Rewards, Body, Shot Reminders) and a **subscription tier** (Food, Move, macros, AI coach). Everything you log syncs to your chart instantly." },
      { kind: "p", text: "**Free features (no subscription needed):**" },
      { kind: "ul", items: [
        "**Rewards tab** — auto-tracks your steps in the background, earn 1 point per step, redeem for coupons + refill credits + merch. Includes shareable tier badges, a leaderboard, and daily push nudges at 10 AM ('You're 1,247 steps from your next reward').",
        "**Body tab** — weight logging, tape measurements (waist/hip/chest/etc.), side-effect chips with severity-graded triage, injection logging with site rotation, and weekly shot reminders (email or SMS at your chosen day + time).",
      ] },
      { kind: "p", text: "**Subscription unlocks (Food + Move tabs and Home dashboard):**" },
      { kind: "ul", items: [
        "**Food tab** — calorie log by meal (breakfast/lunch/dinner/snack), USDA food database search + barcode scanner.",
        "**Move tab** — pick an activity (walking, running, yoga, weights…), set duration + intensity — calories burned auto-estimated via MET × weight × duration.",
        "**Home dashboard** — net calorie ring, macro rings vs. protein/fat/carb goals, Fit-Quiz projection (your actual weight overlaid on the predicted curve).",
        "**AI wellness assistant** — general lifestyle and account questions only. Not medical advice. Every clinical decision is made by a US-licensed clinician on our team, separate from the assistant. Severe symptoms auto-escalate to a human immediately.",
      ] },
      { kind: "ol", items: [
        "From your dashboard, tap **Open Companion App**, or go directly to **/companion/install** to add it to your phone's home screen.",
        "On first launch, opt in to **Auto-track my steps** (uses phone motion sensors while the app is open) and **Daily nudges** (one push at 10 AM).",
        "Log a weight or measurement in the **Body** tab to see it flow into your care team's chart.",
      ] },
      { kind: "tip", text: "Add to Home Screen on your phone (Safari → Share → Add to Home Screen / Chrome → menu → Install app) for an icon on your home screen and a full-screen native-app feel." },
      { kind: "p", text: "**Got a lifetime access code from our team?** If our staff sent you one (looks like `COMP-XXXX-XXXX-XXXX`), head to **Profile → Companion App access code** and paste it in to unlock the subscription-only tabs (Food, Move, AI coach) — even if you're on one-time orders or paused." },
      { kind: "p", text: "**Refer a friend, both get $20 + bonus points** — Yellow banner inside the Companion App home screen. Tap **Share** to send your code via Messages, Mail, or AirDrop. The moment they sign up, you earn a bonus batch of Step-a-Thon points (the exact amount is shown on the banner — the copy auto-updates when our team adjusts it). You *also* get $20 off when they place their first paid order." },
    ],
  },
  {
    id: "wellness",
    icon: Activity,
    title: "Weekly weigh-ins, side effects & the 5-week reward",
    keywords: ["weight", "weigh", "log", "streak", "coupon", "reward", "side effect", "tracker"],
    body: [
      { kind: "p", text: "The Wellness Tracker on your dashboard helps you stay consistent with weekly weigh-ins and gives you a $10 off coupon every 5 consecutive on-time logs (stackable with subscription discount and any other promo)." },
      { kind: "ol", items: [
        "On your dashboard, find **Wellness Tracker** at the top.",
        "Tap the **gear icon** and pick the day of the week you'll weigh in (e.g., Monday).",
        "Each week on that day, tap **Log weight** — that's it. Log on any other day and it's saved but doesn't count toward your streak.",
        "Hit the small chip count of **5/5 weeks** in a row → you earn a $10 off coupon. It's stackable with your subscription discount, marketing codes, and loyalty rewards.",
      ] },
      { kind: "ul", items: [
        "**Maximum reward**: 4 coupons per rolling 12 months ($40/year ceiling).",
        "**Miss your day?** Streak resets to 0 at 12:01 AM the following day — we'll email you so you can start fresh next week.",
        "**Side effects**: tap **Log side effect** anytime, pick the chips and severity. If you mark **Severe** and our chat team is online, we'll connect you instantly. If we're offline, we'll show you our clinical care team phone — for a real medical emergency, always call **911**.",
        "Coupon codes appear in your Wellness Settings → Coupons earned and auto-apply at checkout.",
      ] },
      { kind: "tip", text: "Pick a day you're already routinely home (Sundays work great for most people). Same day, same scale, same time of morning = the most accurate trend." },
    ],
  },
  {
    id: "referrals",
    icon: Gift,
    title: "Referring friends — your hub & rewards",
    keywords: ["refer", "referral", "share", "code", "friend", "$20"],
    body: [
      { kind: "p", text: "When a friend joins GLPFlo using your referral code and completes their first paid order, you both get $20 off." },
      { kind: "ol", items: [
        "On your dashboard, find the purple **Refer a friend** card and click **View hub**.",
        "Your code (e.g., **NGLAQ5OP**) is shown — tap **Copy code** or **Copy link** to share.",
        "Tap **Share via Text** to open your phone's messaging app with a pre-filled invite.",
        "Watch your **Referral history** — pending (friend signed up but hasn't paid) → rewarded (credit on your account) once they complete their first order.",
      ] },
      { kind: "tip", text: "Cap is 5 referrals lifetime — that's $125 in stacked savings. The rewards are stackable with your subscription discount and weigh-in coupons too." },
    ],
  },
  {
    id: "questionnaire",
    icon: ClipboardList,
    title: "Filling out the medical questionnaire",
    keywords: ["questionnaire", "medical", "health", "history", "intake"],
    body: [
      { kind: "p", text: "The medical questionnaire is how your provider determines if a GLP-1 is safe and right for you." },
      { kind: "ol", items: [
        "Click **Medical Questionnaire** in your dashboard sidebar (or on the welcome card if you haven't started yet).",
        "Answer every question honestly — including weight, height, conditions, allergies, and current medications.",
        "Your responses are encrypted and visible only to your assigned provider.",
        "Submit. You'll see a confirmation and can return to your dashboard.",
      ] },
      { kind: "tip", text: "If your health changes (new medication, new diagnosis, weight change >10 lbs), update your questionnaire. There's no penalty — providers prefer current info." },
    ],
  },
  {
    id: "ordering",
    icon: Pill,
    title: "Choosing a medication & tier",
    keywords: ["order", "medication", "subscribe", "buy", "tier", "price"],
    body: [
      { kind: "p", text: "GLPFlo offers two GLP-1 medications across three pricing tiers." },
      { kind: "ul", items: [
        "**Semaglutide with B12** — single GLP-1 receptor agonist, longest real-world track record.",
        "**Tirzepatide with B12** — dual GIP + GLP-1 agonist, higher average weight loss in clinical trials.",
        "**Single vial** — pay once, no commitment.",
        "**Monthly** — one vial shipped each month, save vs single-vial pricing.",
        "**Royal 56-day** — two vials every 56 days, lowest cost per month.",
      ] },
      { kind: "ol", items: [
        "Click **Browse Medications** from the dashboard or use the **New Order** quick action.",
        "Compare both medications and pick the tier that fits your goals.",
        "Click **Subscribe** or **Buy Once** — you'll be taken to a secure Stripe checkout page.",
        "After payment, your order moves to **Pending Review** until a provider approves it.",
      ] },
      { kind: "tip", text: "Not sure which to pick? Visit /medications-info (no login needed) for a side-by-side clinical comparison, or click your active provider's chat icon to ask." },
    ],
  },
  {
    id: "tracking",
    icon: Truck,
    title: "Tracking your shipment",
    keywords: ["tracking", "shipped", "delivery", "where", "package"],
    body: [
      { kind: "p", text: "Once Polomar Specialty Pharmacy generates a shipping label, your order's status changes to **Shipped** and you'll receive an email with a tracking link." },
      { kind: "ol", items: [
        "Watch your inbox — the tracking email has a big purple **Track Your Shipment** button.",
        "On your dashboard, the order card shows the carrier (UPS / USPS / FedEx) and the tracking number.",
        "Click the tracking number on the dashboard to open the carrier's tracking page in a new tab.",
        "Most orders arrive in 2–5 business days. All shipments use temperature-controlled packaging.",
      ] },
      { kind: "tip", text: "Didn't get the email? Check spam, and confirm your email address is correct in Profile → Account. You can also see the tracking number directly on the dashboard." },
    ],
  },
  {
    id: "shipping-coverage",
    icon: Truck,
    title: "Why can't I order? — \"We can't ship to {ST} yet\"",
    keywords: ["state", "ship", "shipping", "coverage", "blocked", "waitlist", "license", "not licensed", "serving", "currently serving"],
    body: [
      { kind: "p", text: "**Check the top strip first.** Every marketing page (home + pricing) shows a **\"Currently serving:\"** strip at the very top with the list of states we can ship to today (e.g. **PA · WI · GA · FL**). If your state isn't listed there, we can't fill your prescription yet. The list updates the moment a new partner-pharmacy license comes online — no need to refresh." },
      { kind: "p", text: "If you see a red banner on the checkout page that says **\"We can't ship to {your state} yet\"**, it means none of our partner pharmacies currently hold a license to ship into your state." },
      { kind: "p", text: "**Why this happens.** Compounded GLP-1 medications can only ship from a pharmacy licensed in your state. We work with one or more partner pharmacies, each with its own state-by-state list — and we expand coverage steadily as new partners come online." },
      { kind: "ol", items: [
        "**Double-check your shipping state.** Profile → Account → Address. A typo (e.g. \"NX\" instead of \"NV\") will trip the banner. Save the corrected state and reload the checkout page — the badge re-checks every 30 seconds.",
        "**Join the waitlist.** Use the **\"Notify me when {ST} ships\"** form right inside the red banner. Drop your email and we'll send a launch-day email the moment a pharmacy partner adds your state to their coverage. No account required.",
        "**Try again later.** If you've signed up for the waitlist you don't have to keep checking — we'll email you automatically.",
      ] },
      { kind: "tip", text: "Already submitted a waitlist signup? You'll see a green confirmation pill instead of the form. Your spot is held — there's nothing more to do. The Pay button stays disabled until your state is covered." },
    ],
  },

  {
    id: "top-strips",
    icon: MessageSquare,
    title: "What are the strips at the top of the page?",
    keywords: ["strip", "banner", "top", "serving", "compare", "million", "mile", "orders paused", "free shipping"],
    body: [
      { kind: "p", text: "The GLPFlo home + pricing pages carry up to five slim strips above the main navigation. Each one is short and skimmable so you get the most important information before you scroll." },
      { kind: "ul", items: [
        "**Orange \"Orders paused\" strip** — only shown when we're temporarily not accepting new orders (e.g. during scheduled pharmacy maintenance). Includes an inline email-me-when-back capture. Existing subscriptions and medication schedules are unaffected during a pause.",
        "**Black COMPARE strip** — quick side-by-side of our starter dose ($79.99), Semaglutide monthly ($149/mo), and Tirzepatide monthly ($249/mo). Tap **See full pricing** to jump to the /pricing page.",
        "**Purple Million-Mile Movement ticker** — live community counter for our nationwide step-a-thon. Shows total steps, miles, and walkers. Tap **Join** to sign up or **Share** to invite friends.",
        "**Dark \"Currently serving:\" strip** — the states where our partner pharmacies are licensed today. See the section above for what happens if your state isn't listed.",
        "**Green rolling marquee** — occasional announcements from GLPFlo (e.g. \"Provider consultation included — transparent pricing, no hidden fees\"). Scrolls left-to-right.",
      ] },
      { kind: "tip", text: "Below the hero image you'll also see a rotating **Free Shipping / No Membership Fees** strip — both are permanent GLPFlo benefits and cycle every ~5 seconds." },
    ],
  },

  {
    id: "receipts",
    icon: Receipt,
    title: "Receipts — view, print, email, forward",
    keywords: ["receipt", "invoice", "hsa", "fsa", "tax", "reimbursement", "accountant"],
    body: [
      { kind: "p", text: "Every paid order generates a downloadable PDF receipt. You'll see a **purple receipt icon** on each paid order on your dashboard, and a full history under **Profile → Receipts**." },
      { kind: "ol", items: [
        "Click any **purple receipt icon** (dashboard) or **View** button (profile receipts list).",
        "A modal opens with the PDF rendered inline.",
        "Use the action bar at the bottom: **Print · Email me a copy · Forward to another email · Share · Download**.",
      ] },
      { kind: "ul", items: [
        "**Email me a copy** — re-sends to your email on file. Useful if you lost the original.",
        "**Forward to another email** — type any address, click Send. Great for HSA/FSA reimbursement, accountants, or family.",
        "**Share** — on iOS/Android, opens the system share sheet (Messages, Mail, AirDrop, etc.) with the PDF attached.",
        "**Download** — saves the PDF to your device.",
        "**Print** — opens the PDF in a new window with your printer's native dialog.",
      ] },
      { kind: "tip", text: "Receipts include the medication name and amount paid — accepted by most HSA/FSA plans for reimbursement. Just forward to your benefits administrator." },
    ],
  },
  {
    id: "subscription",
    icon: CreditCard,
    title: "Managing your subscription",
    keywords: ["subscription", "cancel", "pause", "billing", "renew", "stop"],
    body: [
      { kind: "p", text: "Your subscription is yours to control — pause, change tiers, or cancel any time, no fees." },
      { kind: "ol", items: [
        "Open the **Subscriptions** card on your dashboard.",
        "Click **Manage** on any active subscription.",
        "Choose **Pause** (resume later), **Change tier** (e.g., Monthly → Royal 56-day for better savings), or **Cancel** (stops all future charges immediately).",
      ] },
      { kind: "tip", text: "Cancellation takes effect at the end of your current billing period — you'll still receive whatever you've already paid for." },
    ],
  },
  {
    id: "documents",
    icon: FileText,
    title: "Uploading documents (insurance card, photo ID)",
    keywords: ["documents", "upload", "id", "insurance", "photo"],
    body: [
      { kind: "p", text: "Some patients are asked to upload supporting documents — usually a photo ID, lab results, or a previous prescription." },
      { kind: "ol", items: [
        "Go to **Documents** in your sidebar.",
        "Click **Upload** and pick a file (PDF, JPG, or PNG up to 10 MB).",
        "Tag the file (Photo ID / Lab Result / Other) and confirm.",
        "Your provider is notified automatically.",
      ] },
      { kind: "tip", text: "Documents are encrypted at rest. Only your assigned provider and authorized GLPFlo staff can access them — never shared." },
    ],
  },
  {
    id: "chat",
    icon: MessageSquare,
    title: "Chatting with the GLPFlo team",
    keywords: ["chat", "support", "message", "help", "talk", "question", "agent"],
    body: [
      { kind: "p", text: "Got a non-urgent question? Use the chat bubble in the bottom-right corner of any page after you log in." },
      { kind: "ol", items: [
        "Click the **chat bubble** (bottom-right of every page).",
        "Look at the small pill next to **GLPFlo Support** in the chat header — green **ONLINE** means an agent can reply now, amber **OFFLINE** means the team has stepped away.",
        "If you see green **ONLINE**, just type and send.",
        "If you see amber **OFFLINE**, the chat shows an email link to support — Email replies typically arrive within one business day. (The text input stays disabled so your message can't get lost in an empty queue.)",
        "Once an agent picks up, you'll see their name as your assigned agent — they'll stick with you for that conversation.",
      ] },
      { kind: "tip", text: "For medical emergencies, call **911**. The chat is for non-urgent questions only." },
    ],
  },
  {
    id: "profile",
    icon: Settings,
    title: "Updating your profile & contact info",
    keywords: ["profile", "address", "phone", "email", "password", "settings", "change"],
    body: [
      { kind: "p", text: "Keep your shipping address, phone, and email current — they're how we reach you about prescriptions and shipments." },
      { kind: "ol", items: [
        "Click **Profile** in your sidebar.",
        "Use the **Account** section to update name, email, phone, or password.",
        "Use the **Shipping Address** section to update where your medication ships. Changes apply to your next order — already-shipped orders use their original address.",
      ] },
      { kind: "tip", text: "Changing your email triggers a verification link sent to the new address. The change isn't applied until you click that link." },
    ],
  },
  {
    id: "referral",
    icon: Heart,
    title: "Referring a friend (and earning $20)",
    keywords: ["refer", "referral", "friend", "credit", "earn", "share"],
    body: [
      { kind: "p", text: "Love GLPFlo? Refer a friend and you both earn $20 credit when they place their first paid order." },
      { kind: "ol", items: [
        "Open the **Refer a Friend** card on your dashboard.",
        "Copy your unique referral link or share it via email/text using the built-in share buttons.",
        "When your friend signs up using your link and completes their first paid order, $20 is credited to both of you automatically.",
      ] },
      { kind: "tip", text: "There's no cap on how much you can earn — refer up to 10 friends per year. Credits stack and apply automatically to your next order." },
    ],
  },
  {
    id: "fit-quiz",
    icon: Sparkles,
    title: "\"Am I a good fit?\" 30-second quiz",
    keywords: ["quiz", "fit", "projection", "bmi", "good fit", "am i a fit", "sema", "tirz"],
    body: [
      { kind: "p", text: "Our Fit Quiz lives right at the top of the home page. 30 seconds, tells you whether GLP-1 therapy matches your body + goals, and projects your 90-day weight-loss curve using real clinical-trial data." },
      { kind: "ol", items: [
        "Tap **Am I a good fit? · Take the 30-sec quiz** on the hero.",
        "Enter height + weight (your BMI shows in real time).",
        "Pick your goal (10–20 lb → 50+ lb).",
        "Tell us if you've tried a GLP-1 before. If yes, we ask when you last took it — this is a clinical-safety rule, not a gotcha (see tip).",
        "Tick relevant conditions (T2D, PCOS, prediabetes, high BP, or none).",
        "See your result + tap **Show my 90-day projection** for a personalized weight chart.",
      ] },
      { kind: "tip", text: "**The 2-week rule:** if you haven't taken a GLP-1 in more than 2 weeks, your tolerance has reset and you must restart at the Low dose. This is standard FDA-label titration — we honor it so you avoid the GI side effects that come from ramping up too fast." },
    ],
  },
  {
    id: "fit-quiz-projection",
    icon: LineChart,
    title: "Your 90-day projection chart",
    keywords: ["projection", "chart", "90 days", "weight loss", "trajectory", "step-1", "surmount"],
    body: [
      { kind: "p", text: "After the quiz, tap **Show my 90-day projection** for a personalized weight-loss curve. It uses published clinical-trial averages (STEP-1 for Semaglutide, SURMOUNT-1 for Tirzepatide), scaled to your starting dose with proper 4-week titration." },
      { kind: "ul", items: [
        "Curve starts at your current weight and ends at week 13.",
        "Hover any point to see the projected weight that week.",
        "Tap **Show sources** for the full scientific citations with direct NEJM and PubMed links.",
      ] },
      { kind: "tip", text: "Projections are averages — individual results depend on diet, activity, adherence, and physiology. A US-licensed provider reviews every signup before prescribing, so your actual plan may differ." },
    ],
  },
  {
    id: "fit-quiz-share",
    icon: Share2,
    title: "Share your projection with a spouse, PCP, or HSA admin",
    keywords: ["share", "projection", "spouse", "family", "pcp", "hsa", "email results"],
    body: [
      { kind: "p", text: "Two ways to share your quiz results — both optional, never forced, never added to any marketing list." },
      { kind: "ul", items: [
        "**Share my projection** — opens your phone's native share sheet (Messages, WhatsApp, Mail) with the chart image + signup link. Great for spouses, PCPs, or HSA admins.",
        "**Email my results** — type your email + tap Send. You'll get a formatted email with the chart and full scientific citations.",
      ] },
      { kind: "tip", text: "The emailed version includes a collapsible **Sources & clinical evidence** block with NEJM + PubMed citations inline — perfect for FSA/HSA claims that need medical-evidence backup." },
    ],
  },
  {
    id: "who-accessed",
    icon: Eye,
    title: "\"Who has accessed my record?\" — full transparency",
    keywords: ["access", "privacy", "log", "who viewed", "transparency", "hipaa"],
    body: [
      { kind: "p", text: "Every time a clinician, admin, or support teammate views your record, we log it — and you can see it. No exceptions." },
      { kind: "ol", items: [
        "Open **Profile** from your avatar.",
        "Scroll to **Who has accessed my record?** near the bottom.",
        "Review the last 50 entries: who viewed, role, what they looked at (chart, notes, documents, wellness logs), timestamp, and any **reason** they entered to reveal sensitive info like DOB or SSN.",
      ] },
      { kind: "tip", text: "If you see an access that seems inappropriate — someone viewing your record outside a clear clinical reason — email customer.service@glpflo.com. We investigate within 24 hours and follow HIPAA's breach-notification process if anything is confirmed." },
    ],
  },
  {
    id: "download-my-data",
    icon: Download,
    title: "Download all your data (HIPAA § 164.524 right of access)",
    keywords: ["download", "data export", "my data", "hipaa", "right of access", "zip"],
    body: [
      { kind: "p", text: "Federal law gives you the right to a complete copy of your health record at any time. GLPFlo makes this one click." },
      { kind: "ol", items: [
        "Go to **Profile** → scroll to the *Who has accessed my record?* card.",
        "Click the purple **Download all my data (HIPAA § 164.524)** button.",
        "A ZIP downloads containing: profile, orders, questionnaires, wellness logs, document list, and your full access log — all readable JSON with a README.",
      ] },
      { kind: "tip", text: "Use this ZIP for switching providers, insurance appeals, HSA/FSA claims, or your own records. Free, as often as you like, no expiration." },
    ],
  },
  {
    id: "pause-notice",
    icon: PauseCircle,
    title: "What does the yellow \"System notice\" banner mean?",
    keywords: ["pause", "notice", "banner", "maintenance", "pharmacy", "closed"],
    body: [
      { kind: "p", text: "If you see an amber/yellow **System notice** banner at the top of any page, our partner pharmacy or our platform is temporarily pausing *new* orders." },
      { kind: "ul", items: [
        "Your **existing subscription** is NOT affected — scheduled refills go out on time.",
        "Shipments already in transit arrive on their normal schedule.",
        "You can keep logging weigh-ins, messaging your clinician, and viewing documents.",
        "Only **Place order** and **Request refill** buttons are paused — they re-enable automatically within 60 seconds of the pause lifting.",
      ] },
      { kind: "tip", text: "The banner shows the expected resume time. If it's been gone more than a few hours past the window, email customer.service@glpflo.com — we'll help directly." },
    ],
  },
  {
    id: "security-summary",
    icon: ShieldCheck,
    title: "How we protect your information (and what you control)",
    keywords: ["security", "privacy", "hipaa", "2fa", "mfa", "sessions", "password"],
    body: [
      { kind: "p", text: "Your profile page is also your security control panel. Everything below is one click from a safer account." },
      { kind: "ul", items: [
        "**Two-Factor Authentication (MFA)** — takes 60 seconds, protects you from password breaches elsewhere on the web.",
        "**Active Sessions** — see every device signed in. Sign out any with a tap.",
        "**Who has accessed my record?** — every staff view, visible to you.",
        "**Download all my data** — your HIPAA § 164.524 one-click export.",
        "**Receipts** — view, print, email, or forward any payment receipt.",
      ] },
      { kind: "tip", text: "We're HIPAA-compliant and your data is encrypted at rest and in transit. We never sell your information — full stop." },
    ],
  },
  {
    id: "browse-medications-public",
    icon: Pill,
    title: "Browse our medications (no sign-in required)",
    keywords: ["browse", "medications", "catalog", "public", "no signin", "explore", "shop"],
    body: [
      { kind: "p", text: "Curious what we offer before creating an account? **/browse-medications** is a public catalog with every medication we currently prescribe — name, type, dosage, available strengths, and a plain-English description." },
      { kind: "ol", items: [
        "From the footer of any page, click **Browse medications**.",
        "Read each card to learn about the medication, dosage cadence (e.g. weekly injection), and available strengths.",
        "When you're ready, click **Start the quiz** to begin our 30-second eligibility quiz — no commitment.",
      ] },
      { kind: "tip", text: "Pricing isn't shown on this page — it's revealed after the quick eligibility quiz so we can match you with the right plan. Visit **/pricing** any time for a full price breakdown." },
    ],
  },
  {
    id: "share-your-story-email",
    icon: Sparkles,
    title: "Share your story — you may receive an invite by email",
    keywords: ["testimonial", "review", "story", "share", "results", "before after"],
    body: [
      { kind: "p", text: "If you've hit a meaningful milestone — like losing 20+ pounds or being with us 12+ weeks — we may email you a one-time secure link to share your story privately with our team." },
      { kind: "ol", items: [
        "Look for an email from us titled *\"Share your GLPFlo journey\"*.",
        "Click the personal link — it opens **/share-your-story/...** with a short form.",
        "Pick a star rating, write what worked for you, optionally attach a before/after photo.",
        "Choose how you'd like to appear: **Full name**, **First name + last initial**, or **Anonymous**.",
        "Check the release box (you control your words; we control nothing without your written sign-off).",
        "Submit. Your story stays private — GLPFlo does not currently publish patient testimonials on the public site.",
      ] },
      { kind: "tip", text: "You can email us at any time to withdraw your submission — we honor that immediately, no questions." },
    ],
  },
  {
    id: "cookie-settings",
    icon: ShieldCheck,
    title: "Cookies + your privacy choices",
    keywords: ["cookies", "tracking", "consent", "gdpr", "ccpa", "analytics", "privacy"],
    body: [
      { kind: "p", text: "On your first visit you saw a **Cookie banner** with three choices: *Accept all*, *Reject all*, or *Customize*. Your choice was saved to your device — you can change it any time." },
      { kind: "ol", items: [
        "Scroll to the very bottom of any page.",
        "Click **Cookie settings** (next to the Privacy + Terms links).",
        "The banner re-opens. Pick a new preference or fine-tune categories via **Customize**.",
      ] },
      { kind: "p", text: "Want to read every third-party tool we use and what it collects? Visit **/online-tracking** — plain-English disclosures for each, including retention periods and opt-out instructions." },
      { kind: "tip", text: "Strictly-necessary cookies (sign-in, theme, language) always work — they're required for the site to function. Everything else (analytics, error tracking) ONLY runs if you opt in." },
    ],
  },
  {
    id: "online-tracking-disclosure",
    icon: Eye,
    title: "Plain-English disclosure of what we track",
    keywords: ["tracking", "third party", "analytics", "sentry", "posthog", "stripe", "transparency", "hipaa"],
    body: [
      { kind: "p", text: "Per HIPAA's Online Tracking Bulletin, we publicly disclose every third-party tracking technology used on the site. Visit **/online-tracking** for the full table." },
      { kind: "ul", items: [
        "**PostHog** (product analytics) — anonymized event tracking, only if you opt in via the cookie banner.",
        "**Sentry** (error reporting) — captures crashes so we can fix them faster. PHI is stripped before send.",
        "**Stripe** (payments) — you enter your card on Stripe's own secure page; we never see your card number.",
        "**Vonage** (when we call you) — call duration logs only, never call audio.",
      ] },
      { kind: "tip", text: "If you opted out, **none of the optional trackers load**. The page won't even establish a connection to them." },
    ],
  },
  {
    id: "telehealth-glp1-consent",
    icon: ClipboardList,
    title: "Telehealth + GLP-1 consent — what you'll be asked to sign",
    keywords: ["consent", "telehealth", "informed consent", "glp1", "agreement", "checkout"],
    body: [
      { kind: "p", text: "Before your first order, you'll see two short consent modals. They're required by HIPAA and good medicine — please read carefully." },
      { kind: "ul", items: [
        "**Telehealth consent** — acknowledges that this is asynchronous telemedicine, that you can discontinue care at any time, and lists risks/benefits of remote care.",
        "**GLP-1 informed consent** — specific contraindications: pancreatitis history, gallbladder disease, thyroid C-cell tumors / MEN-2 family history, pregnancy / breastfeeding.",
      ] },
      { kind: "p", text: "Both are one-time per account. Once signed, you won't see them again — unless we update the legal language (rare). Your consent is stamped with a timestamp and visible in your **Profile → Privacy** section." },
      { kind: "tip", text: "If you're not sure whether a condition disqualifies you, leave the questionnaire detailed and your provider will reach out before approving anything." },
    ],
  },
  // -------------------------------------------------------------------
  // 2026-08 additions — new patient-facing features added recently.
  // -------------------------------------------------------------------
  {
    id: "stepathon-store",
    icon: Trophy,
    title: "Step-a-Thon Store — turn steps into real rewards",
    keywords: ["stepathon", "step", "store", "rewards", "points", "coupon", "redeem", "amazon", "starbucks", "gift card"],
    body: [
      { kind: "p", text: "Every synced step earns Step-a-Thon points. Cash them in for **coupon codes** to real brands (Amazon, Starbucks, DoorDash and more) or GLPFlo credits — all from the **Store** tab on the Step-a-Thon page." },
      { kind: "ol", items: [
        "Go to **Step-a-Thon → Store** (on the app: bottom nav → Step-a-Thon → Store tab).",
        "See your live points balance at the top. Tap any reward tile to view details.",
        "Confirm the redemption — points come out of your balance immediately.",
        "The coupon code appears in **History** and is **tap-to-copy** so you can paste it at checkout.",
      ] },
      { kind: "tip", text: "**Watch the Getting Started videos** for a one-time **10,000 point** bonus — enough to redeem several rewards right away. Find it under Step-a-Thon → *Watch to earn* (or on web: `/getting-started`)." },
    ],
  },
  {
    id: "delivery-window",
    icon: Truck,
    title: "Request a 3-day delivery window (traveling? out of town?)",
    keywords: ["delivery", "window", "shipping", "travel", "vacation", "reschedule", "3 day", "hold"],
    body: [
      { kind: "p", text: "Heading out of town or want your next shipment to land within a specific 3-day range? Request a **one-time delivery window** and we'll do our best to land your next refill inside it." },
      { kind: "ol", items: [
        "**Dashboard → Traveling? Request a delivery window** (or **Delivery Window** button on the app home).",
        "Pick a **start date** — the window runs 3 days from there.",
        "Add an optional note if there's context (\"I'll be home by the 12th\", \"leave with concierge\", etc.).",
        "Submit — the request goes straight to our shipping team. You'll see it acknowledged in your Documents.",
      ] },
      { kind: "tip", text: "One request per shipment. If you need multiple windows, message support and we'll coordinate manually." },
    ],
  },
  {
    id: "food-exercise-logging",
    icon: Activity,
    title: "Log meals & workouts — Wellness tab",
    keywords: ["food", "meal", "calories", "macros", "protein", "workout", "exercise", "activity", "log"],
    body: [
      { kind: "p", text: "The **Wellness** tab (mobile app) has full food & exercise logging — track your calories, macros, and workouts alongside your GLP-1 journey. Everything you log is private to you and shows up in your care team's dashboard so they can spot trends." },
      { kind: "ol", items: [
        "**Wellness → Log a meal** — enter the food, portion size, and macros (protein/carbs/fat auto-fill for common items).",
        "**Wellness → Log a workout** — pick from cardio, strength, walking, or custom. Enter duration and estimated calories burned.",
        "Both feeds show your last 7 days at the top — pull down to refresh.",
      ] },
      { kind: "tip", text: "If you connect **Google Health** or Apple Health (iOS), workouts and step counts sync automatically — no manual entry needed." },
    ],
  },
  {
    id: "symptom-checkin",
    icon: Heart,
    title: "Symptom check-in — even between subscriptions",
    keywords: ["symptom", "side effect", "checkin", "check-in", "nausea", "fatigue", "log symptom"],
    body: [
      { kind: "p", text: "Not feeling great? Log a symptom check-in from the app home screen. It goes to your care team the same day — even if your subscription is paused." },
      { kind: "ol", items: [
        "**Home → Symptom Check-In** (red banner at top of Dashboard).",
        "Pick the symptom, severity, and add optional notes.",
        "Submit — you'll get a confirmation, and someone from care will reach out if it's severe.",
      ] },
      { kind: "tip", text: "Severe or worsening symptoms are automatically escalated. If you're having chest pain, difficulty breathing, or signs of allergic reaction, call **911** or your local emergency line first." },
    ],
  },
  {
    id: "appearance-theme",
    icon: Settings,
    title: "Light / Dark / System theme (mobile app)",
    keywords: ["theme", "dark", "light", "appearance", "night", "mode", "color scheme"],
    body: [
      { kind: "p", text: "The mobile app supports a **Light**, **Dark**, or **System** appearance — flip anytime from Profile." },
      { kind: "ol", items: [
        "Open the app → **Profile** tab.",
        "Scroll to **Preferences → Appearance**.",
        "Tap **Light**, **Dark**, or **System** (follows your phone's OS setting, including auto-switch at sunset if you have that enabled).",
      ] },
      { kind: "tip", text: "The change applies instantly across every screen. If you prefer a completely silent boot animation after sign-in, flip the **Boot animation sound** switch just below Appearance." },
    ],
  },
  {
    id: "hide-chat-widget",
    icon: MessageSquare,
    title: "Hide the chat widget completely (web & mobile)",
    keywords: ["chat", "hide", "minimize", "widget", "bubble", "dismiss"],
    body: [
      { kind: "p", text: "The purple chat launcher normally sits in the bottom-right corner on web (and above the tab bar on mobile). If you want it gone entirely, both apps let you hide it — it stays hidden for the rest of the session, and reappears when you refresh the page or reopen the app." },
      { kind: "ol", items: [
        "Open the chat panel (tap the purple pill).",
        "In the header, tap the **eye/hide** icon (leftmost). The panel closes **and** the launcher disappears.",
        "To bring it back on web: refresh the page or click the tiny \"?\" help button, then select \"Show chat launcher\".",
        "To bring it back on mobile: pull-to-refresh on Home or reopen the app.",
      ] },
      { kind: "tip", text: "The regular X still just closes the panel — the launcher stays visible. Use the hide icon only when you want the widget completely out of your way." },
    ],
  },
];

const PUBLIC_PATHS = ["/login", "/register", "/forgot-password", "/reset-password", "/verify-email-change", "/", "/onboarding", "/questionnaire"];

export default function PatientHelpGuide() {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("first-time");
  const [search, setSearch] = useState("");
  const [seen, setSeen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // First-visit pulse: once-per-account hint that the help button exists.
  useEffect(() => {
    try {
      const flag = localStorage.getItem("glpflo-help-seen");
      setSeen(flag === "1");
    } catch { /* ignore */ }
  }, []);

  const markSeen = () => {
    try { localStorage.setItem("glpflo-help-seen", "1"); } catch { /* ignore */ }
    setSeen(true);
  };

  // Don't render on public/onboarding pages.
  const isPublic = PUBLIC_PATHS.includes(location.pathname);

  // While another Radix dialog is open (consent modal, share modal, etc.)
  // hide the launcher so it can't cover modal content. Our own dialog
  // sets data-state="open" too, but we short-circuit by checking `open`
  // first (we always want to keep the button hidden while OUR modal is
  // showing anyway).
  const anyDialogOpen = useAnyDialogOpen();
  const buttonHidden = isPublic || anyDialogOpen;

  // Register in the bottom-right stack so we always sit above whatever
  // currently occupies the corner — chat (priority 30), FAB (20), cookie
  // banner (10), and any future floating CTA. `visible` flips to false
  // when the component would short-circuit return null below, removing
  // us from the stack so the chat doesn't get pushed up for nothing.
  const helpBottomPx = useBottomRightStack({
    id: "patient-help-guide",
    priority: 40,
    visible: !buttonHidden,
    height: 48, // 12*4 = 48px button
  });

  if (isPublic) return null;

  const filtered = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return GUIDES;
    return GUIDES.filter(g =>
      g.title.toLowerCase().includes(q) ||
      (g.keywords || []).some(k => k.includes(q)) ||
      g.body.some(b => (b.text || "").toLowerCase().includes(q) || (b.items || []).some(i => i.toLowerCase().includes(q)))
    );
  })();

  // Keep activeId valid when the search filter changes.
  const activeGuide = filtered.find(g => g.id === activeId) || filtered[0] || GUIDES[0];

  const renderBlock = (block, idx) => {
    if (block.kind === "p") {
      return <p key={idx} className="text-sm text-slate-700 leading-relaxed mb-3" dangerouslySetInnerHTML={{ __html: bold(block.text) }} />;
    }
    if (block.kind === "ol") {
      return (
        <ol key={idx} className="list-decimal pl-5 space-y-1.5 text-sm text-slate-700 mb-3 marker:text-purple-400 marker:font-semibold">
          {block.items.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: bold(it) }} />)}
        </ol>
      );
    }
    if (block.kind === "ul") {
      return (
        <ul key={idx} className="list-disc pl-5 space-y-1.5 text-sm text-slate-700 mb-3 marker:text-purple-300">
          {block.items.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: bold(it) }} />)}
        </ul>
      );
    }
    if (block.kind === "tip") {
      return (
        <div key={idx} className="mt-3 mb-3 bg-purple-50 border-l-4 border-purple-400 rounded-r-lg p-3 text-sm text-purple-900">
          <span className="text-[10px] font-bold uppercase tracking-wider text-purple-600 mr-1.5">Pro tip</span>
          <span dangerouslySetInnerHTML={{ __html: bold(block.text) }} />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {!buttonHidden && (
      <button
        type="button"
        onClick={() => { setOpen(true); markSeen(); }}
        className="fixed right-6 z-40 w-12 h-12 rounded-full bg-white border border-purple-200 hover:border-purple-400 text-purple-700 shadow-[0_8px_30px_rgba(124,58,237,0.25)] hover:shadow-[0_12px_40px_rgba(124,58,237,0.4)] transition-[bottom] duration-300 flex items-center justify-center group"
        style={{ bottom: `${helpBottomPx}px` }}
        aria-label="Open help guide"
        data-testid="patient-help-button"
        title="Need help? Open the guide"
      >
        <HelpCircle className="w-5 h-5 transition-transform group-hover:scale-110" />
        {!seen && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full ring-2 ring-white animate-pulse" />
        )}
      </button>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="patient-help-dialog">
          <DialogHeader className="px-6 pt-5 pb-4 border-b bg-gradient-to-br from-purple-50 to-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center shrink-0">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>How-to guide</DialogTitle>
                <DialogDescription className="text-xs text-slate-500 mt-0.5">Quick walkthroughs for everything you can do in your patient portal.</DialogDescription>
              </div>
            </div>
            <div className="mt-3 relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search the guide…"
                className="w-full text-sm rounded-full border border-slate-200 bg-white py-2 pl-9 pr-3 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
                data-testid="help-search-input"
              />
            </div>
          </DialogHeader>

          <div className="flex-1 min-h-0 flex flex-col sm:grid sm:grid-cols-[220px_1fr]">
            {/* Sidebar — on mobile, a horizontal-scroll chip strip so it
                doesn't eat the article's vertical space. On sm+ becomes
                a vertical list. */}
            <nav className="border-b sm:border-b-0 sm:border-r border-slate-100 bg-slate-50/40 sm:overflow-y-auto max-h-[35vh] sm:max-h-none overflow-y-auto" data-testid="help-sidebar">
              {filtered.length === 0 ? (
                <p className="px-4 py-6 text-xs text-slate-400 italic">No guides match &quot;{search}&quot;.</p>
              ) : (
                <ul className="py-2">
                  {filtered.map((g) => {
                    const Icon = g.icon || HelpCircle;
                    const isActive = g.id === activeGuide.id;
                    return (
                      <li key={g.id}>
                        <button
                          type="button"
                          onClick={() => setActiveId(g.id)}
                          className={`w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm transition-colors border-l-2 ${isActive ? "bg-white border-purple-500 text-purple-900 font-medium" : "border-transparent text-slate-600 hover:bg-white hover:text-slate-900"}`}
                          data-testid={`help-nav-${g.id}`}
                        >
                          <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-purple-600" : "text-slate-400"}`} />
                          <span className="truncate">{g.title}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </nav>

            {/* Content */}
            <article className="flex-1 min-h-0 overflow-y-auto px-6 py-5 pb-24 sm:pb-5" data-testid={`help-content-${activeGuide?.id || "none"}`}>
              {activeGuide && (
                <>
                  <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{activeGuide.title}</h3>
                  {activeGuide.body.map((b, i) => renderBlock(b, i))}
                </>
              )}
            </article>
          </div>

          <div className="border-t bg-white px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2 flex-shrink-0" data-testid="help-footer">
            <p className="text-xs text-slate-500">Still stuck? <button type="button" onClick={() => { setOpen(false); navigate("/profile"); }} className="text-purple-700 hover:underline">Visit your profile</button> or reopen the guide from the help button.</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setActiveId(prevSibling(filtered, activeGuide.id)); }} disabled={filtered.length < 2} data-testid="help-prev">
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <Button size="sm" className="rounded-full bg-purple-600 hover:bg-purple-700 text-white" onClick={() => { setActiveId(nextSibling(filtered, activeGuide.id)); }} disabled={filtered.length < 2} data-testid="help-next">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Tiny helpers
function bold(text) {
  // Convert **bold** markers to <strong>; escape other HTML.
  // Already safe by construction (we escape < & > first, then re-inject only
  // <strong class="text-slate-900"> tags), but we additionally pipe through
  // DOMPurify with a strict allowlist to satisfy security scanners and
  // future-proof against any prompt-style injection bypass.
  const safe = (text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const html = safe.replace(/\*\*(.+?)\*\*/g, '<strong class="text-slate-900">$1</strong>');
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: ["strong"], ALLOWED_ATTR: ["class"] });
}

function nextSibling(list, id) {
  if (!list.length) return id;
  const i = list.findIndex(g => g.id === id);
  return list[(i + 1 + list.length) % list.length].id;
}
function prevSibling(list, id) {
  if (!list.length) return id;
  const i = list.findIndex(g => g.id === id);
  return list[(i - 1 + list.length) % list.length].id;
}
